import {
  CurrentAuthURL,
  OwnerName,
} from "./authSelectors";
import { DemoMode, ShowTour } from "./uiAtoms";
import { atom, selector } from "recoil";
import { getStackFromCfKey, strings } from "@quickpoint.me/shared";

import { Authorization } from "./authAtoms";
import { StackCfKeyAtom } from "./stackAtoms";
import { createBrowserHistory } from "history";

// adjust relative path if needed


/* One shared history instance (SSR-safe) */
const history =
  typeof window !== "undefined" ? createBrowserHistory() : undefined;

/* ───────────────────────── rawLocationAtom ───────────────────────────
   – mirrors <pathname,search>
   – effect updates the atom when the user navigates (back / forward)
   – same effect pushes to history when the atom is changed by code      */
export const rawLocationAtom = atom({
  key: "rawLocationAtom",
  default: {
    pathname: history?.location.pathname ?? "/",
    search: history?.location.search ?? "",
  },
  effects: [
    ({ setSelf, onSet }) => {
      /* history ➜ atom */
      const unListen = history?.listen(({ location }) =>
        setSelf({ pathname: location.pathname, search: location.search }),
      );

      /* atom ➜ history (guard against loops) */
      let last = history?.location.pathname + (history?.location.search ?? "");
      onSet((loc) => {
        const next = loc.pathname + loc.search;
        if (history && next !== last) {
          //   console.log("rawLocationAtom: pushing to history", next);
          history.push({ pathname: next, search: "" });
          last = next;
        }
      });

      return () => unListen?.();
    },
  ],
});

/* ───────────────────────── routerSelector ────────────────────────────
   – derives the *desired* path from app state
   – as a side-effect pushes that path into rawLocationAtom so the
     atom-effect above writes to history                                        */
export const routerSelector = selector<string>({
  key: "routerSelector",

  // pure function: derive path from state only
  get: ({ get }) => {
    const auth = get(Authorization);
    const demoMode = get(DemoMode);
    const showTour = get(ShowTour);
    const authUrl = get(CurrentAuthURL);
    const ownerName = get(OwnerName);

    const cfKey = get(StackCfKeyAtom);
    const hasValidKey = getStackFromCfKey(cfKey).validStackCfKey;
    const hasOwner = ownerName !== strings.common.unauthorized;

    /* decision tree copied from old UrlManager ------------------------ */
    let target: string;
    // console.log("routerSelector: demoMode", demoMode);
    // console.log("routerSelector: showTour", showTour);
    // console.log("routerSelector: authUrl", authUrl);
    // console.log("routerSelector: hasValidKey", hasValidKey);

    if (demoMode && showTour) {
      target = `/${strings.demo.cfKey}`;
    } else if (!auth.isLoggedIn && authUrl) {
      target = `/${authUrl}`;
    } else if (!auth.isLoggedIn) {
      // landing screen lives at the root; /login is the choices screen
      target = "/";
    } else if (auth.isLoggedIn && hasOwner && !hasValidKey) {
      target = `/${ownerName}/${strings.unset}`;
    } else if (auth.isLoggedIn && hasOwner && hasValidKey) {
      target = `/${cfKey}`;
    } else {
      target = get(rawLocationAtom).pathname;   // stay put
    }
    // console.log("routerSelector: target", target);
    return target;
  },


});
